import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useParams, useNavigate } from 'react-router';
import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';
import { styled } from '@mui/system';
import Paper from '@mui/material/Paper';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@components/Toaster/Alert';
import Button from '@components/Button';
import { AuthLayout } from '@features/AuthLayout';
import { AuthProgress } from '@components/AuthProgress';
import { useTranslation, useYupValidationResolver, useQueryParams } from '@hooks';
import { Input } from '@components/Input';
import { Typography } from '@components/Typography';
import { GoogleNativeDarkIcon } from 'assets/images/icons';
import { regexAnyCharAtLeastOneNumberAndLetter } from '@constants/regex';
import { RoutePath } from '@constants/routes';
import { getGoogleOAuthAcceptInvitationUrl } from '@constants/integrations';
import { BRAND_TEAL } from '@constants/colors';
import { handleApiError } from '@helpers/api';
import { authService } from '@services';
import { authActions } from '@actions';
import { setUserInLocalStorage } from '@helpers/auth';
import SentryManager from '@widgets/sentry-manager';
import PostHogManager from '@widgets/posthog-manager';

const Form = styled(Paper)({
    marginBottom: '24px',
    padding: '24px',
    width: 400,
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'center',
    '@media (max-width: 600px)': {
        padding: '16px',
        width: '100%',
    },
});

const Overlay = styled('div')({
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: 'rgba(255,255,255,0.6)',
    zIndex: 2,
});

const StyledGoogleButton = styled(Button)({
    backgroundColor: '#4285F4',
    marginBottom: '16px',
    minWidth: 'fit-content',
    '&:hover': {
        backgroundColor: '#2472f2',
    },
    '& .MuiButton-startIcon': {
        display: 'block',
    },
});

const ButtonIconWrapper = styled('div')({
    backgroundColor: '#fff',
    height: 30,
    width: 32,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderBottomLeftRadius: 3,
    borderTopLeftRadius: 3,
    position: 'absolute',
    left: 1,
});

const ProgressGoogleWrapper = styled('div')({
    position: 'absolute',
    zIndex: 3,
    left: 0,
    right: 0,
    display: 'flex',
    alignItems: 'center',
    bottom: 52,
    top: 0,
    background: 'rgba(255, 255, 255, .7)',
    '& .MuiCircularProgress-root': {
        color: BRAND_TEAL,
        width: '58px !important',
        height: '58px !important',
        display: 'block',
        margin: '0 auto 24px',
    },
});

const SESSION_STORAGE_KEY = 'acceptInvitationToken';

const AcceptInvitationPage = () => {
    const { t } = useTranslation();
    const { token } = useParams();
    const query = useQueryParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [googleLoading, setGoogleLoading] = useState(false);
    const [invitationInfo, setInvitationInfo] = useState(null);
    const [error, setError] = useState(null);

    const AcceptInvitationSchema = yup.object().shape({
        password: yup
            .string()
            .min(8, 'validation.minPasswordLength')
            .matches(regexAnyCharAtLeastOneNumberAndLetter, 'validation.wrongPasswordFormat')
            .required('validation.required'),
    });

    const resolver = useYupValidationResolver(AcceptInvitationSchema);

    const {
        getValues,
        handleSubmit,
        control,
        formState: { isValid, isDirty, isSubmitting },
    } = useForm({
        mode: 'all',
        resolver,
        defaultValues: {
            password: '',
        },
    });

    useEffect(() => {
        const code = query.get('code');
        const googleError = query.get('error');

        if (googleError) {
            const storedToken = sessionStorage.getItem(SESSION_STORAGE_KEY) || token;
            sessionStorage.removeItem(SESSION_STORAGE_KEY);
            if (storedToken) {
                navigate(`${RoutePath.AcceptInvitation}/${storedToken}`, { replace: true });
                validateToken(storedToken);
            } else {
                navigate(RoutePath.Login);
            }
            return;
        }

        if (code) {
            const storedToken = sessionStorage.getItem(SESSION_STORAGE_KEY);
            if (storedToken) {
                handleGoogleCallback(storedToken, code);
            } else {
                navigate(RoutePath.Login);
            }
            return;
        }

        if (!token) {
            navigate(RoutePath.Login);
            return;
        }

        validateToken(token);
    }, []);

    const validateToken = async invitationToken => {
        try {
            const info = await authService.validateInvitation(invitationToken);
            setInvitationInfo(info);
        } catch (err) {
            if (err?.errorCode === 'invitation_already_accepted') {
                setError(t('AcceptInvitationPage.alreadyAccepted'));
            } else if (err?.errorCode === 'invitation_token_invalid') {
                setError(t('AcceptInvitationPage.tokenExpired'));
            } else {
                navigate(RoutePath.Login);
            }
        }
    };

    const handleGoogleCallback = async (storedToken, code) => {
        setGoogleLoading(true);
        try {
            const info = await authService.validateInvitation(storedToken);
            setInvitationInfo(info);
        } catch {
            sessionStorage.removeItem(SESSION_STORAGE_KEY);
            navigate(RoutePath.Login);
            return;
        }

        try {
            const redirectUri = `${window.location.origin}${RoutePath.AcceptInvitation}`;
            const user = await authService.acceptInvitationWithGoogle(storedToken, code, redirectUri);
            sessionStorage.removeItem(SESSION_STORAGE_KEY);
            setUserInLocalStorage(user);
            PostHogManager.identify(user);
            SentryManager.setUser(user);
            dispatch(authActions.setUser(user));
        } catch (err) {
            setGoogleLoading(false);
            if (err?.errorCode === 'invitation_email_mismatch') {
                setError(t('AcceptInvitationPage.emailMismatch'));
            } else {
                handleApiError(err);
            }
            navigate(`${RoutePath.AcceptInvitation}/${storedToken}`, { replace: true });
            sessionStorage.removeItem(SESSION_STORAGE_KEY);
        }
    };

    const onGoogleClick = () => {
        sessionStorage.setItem(SESSION_STORAGE_KEY, token);
        window.location = getGoogleOAuthAcceptInvitationUrl();
    };

    const onSubmit = async () => {
        const { password } = getValues();
        setError(null);
        try {
            const user = await authService.acceptInvitation(token, password);
            setUserInLocalStorage(user);
            PostHogManager.identify(user);
            SentryManager.setUser(user);
            dispatch(authActions.setUser(user));
        } catch (err) {
            handleApiError(err);
        }
    };

    return (
        <AuthLayout>
            <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                {googleLoading && (
                    <ProgressGoogleWrapper>
                        <CircularProgress />
                    </ProgressGoogleWrapper>
                )}
                {isSubmitting && <AuthProgress />}
                <Form elevation={24}>
                    {isSubmitting && <Overlay />}
                    <Typography variant="h5" sx={{ marginBottom: '8px', color: 'rgba(0, 0, 0, .87)' }}>
                        {t('AcceptInvitationPage.title')}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>
                        {t('AcceptInvitationPage.description')}
                    </Typography>
                    {invitationInfo?.email && (
                        <Input
                            inputProps={{ value: invitationInfo.email, readOnly: true }}
                            label={t('common.emailAddress')}
                            fullWidth
                            disabled
                            sx={{ marginBottom: '16px' }}
                        />
                    )}
                    {error && (
                        <Alert severity="error" sx={{ width: '100%', marginBottom: '16px' }}>
                            <Typography variant="body2" sx={{ color: 'inherit' }}>
                                {error}
                            </Typography>
                        </Alert>
                    )}
                    <StyledGoogleButton
                        type="button"
                        variant="contained"
                        fullWidth
                        onClick={onGoogleClick}
                        disabled={googleLoading}
                    >
                        <ButtonIconWrapper>
                            <GoogleNativeDarkIcon />
                        </ButtonIconWrapper>
                        {t('AcceptInvitationPage.continueWithGoogle')}
                    </StyledGoogleButton>
                    <div style={{ display: 'flex', alignItems: 'center', width: '100%', marginBottom: '16px' }}>
                        <span style={{ borderTop: '1px solid rgba(0, 0, 0, 0.12)', width: '100%' }} />
                        <Typography variant="caption" color="textSecondary" style={{ padding: '0 16px' }}>
                            {t('common.or')}
                        </Typography>
                        <span style={{ borderTop: '1px solid rgba(0, 0, 0, 0.12)', width: '100%' }} />
                    </div>
                    <Controller
                        control={control}
                        name="password"
                        render={({ field, fieldState: { error: fieldError }, ref }) => (
                            <Input
                                ref={ref}
                                inputProps={{ ...field }}
                                type="password"
                                label={t('common.password')}
                                sx={{ marginBottom: '16px' }}
                                fullWidth
                                error={!!fieldError}
                                helperText={fieldError ? t(fieldError.message) : null}
                            />
                        )}
                    />
                    <Button
                        onClick={handleSubmit(onSubmit)}
                        variant="contained"
                        fullWidth
                        disabled={isSubmitting || !isValid || !isDirty}
                    >
                        {t('buttons.signupButton')}
                    </Button>
                </Form>
            </div>
        </AuthLayout>
    );
};

export default AcceptInvitationPage;
